import { Dispatch } from 'redux';
import fetchApi from '../services/api';
import * as types from './actionTypes';
import track from '../lib/track';
import { forceLoginHelper } from './auth';
import { getOrCreateStore } from '../lib/with-redux-store';
import {
  setFirstFavoriteCompleted,
  setFirstSearchCompleted,
} from './dashboard';

/**
 * Fetch a user's starred listings.
 * @param {string} token - A user's authentication token
 */
export function fetchFavorites(
  token: string,
  offset: number = 0,
  limit: number = 100,
  reset: boolean = false,
) {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      type: types.ClearMessages,
    });
    dispatch({
      type: types.SetFavoritesLoading,
    });
    if (reset) {
      dispatch({
        type: types.ClearFavorites,
      });
    }
    try {
      const response = await fetchApi(
        `/favorites?offset=${offset}&limit=${limit}`,
        {},
        'get',
        {},
        token,
      );
      const json = await response.json();
      if (response.ok) {
        if (json.results.length > 0) {
          dispatch(setFirstFavoriteCompleted());
          // Since property is favorited, its obvious that the user has done first search
          dispatch(setFirstSearchCompleted());
        }
        return dispatch({
          favorites: json.results,
          messages: [json],
          type: types.FavoritesFetchSuccess,
        });
      } else {
        return dispatch(
          forceLoginHelper(json, () => {
            dispatch({
              messages: [json],
              type: types.FavoritesFetchFailure,
            });
          }),
        );
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

/**
 * Save (star) a favorite listing for a user.
 * @param {number}  mlsID - A listing's MLS ID
 * @param {string} token - A user's authentication token
 */
export function saveFavorite(mlsId: number, incomingToken: string) {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      type: types.ClearMessages,
    });
    try {
      const store = getOrCreateStore();
      const authStore = store.getState().auth;
      if (!incomingToken && !authStore) {
        return;
      }
      const { token } = authStore;
      const response = await fetchApi(
        `/favorites/${mlsId}`,
        {},
        'post',
        {},
        incomingToken ? incomingToken : token,
      );
      const json = await response.json();
      if (response.ok) {
        dispatch(setFirstFavoriteCompleted());
        track.favoritedListing(mlsId);
        return dispatch({
          listing: json.listing,
          messages: [json],
          type: types.FavoritesSaveSuccess,
        });
      } else {
        return dispatch(
          forceLoginHelper(json, () => {
            dispatch({
              messages: [json],
              type: types.FavoritesSaveFailure,
            });
          }),
        );
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

/**
 * Delete (unstar) a favorite listing for a user.
 * @param {number}  mlsID - A listing's MLS ID
 * @param {string} token - A user's authentication token
 */
export function deleteFavorite(mlsId: number, token: string) {
  return async (dispatch: Dispatch<any>) => {
    dispatch({
      type: types.ClearMessages,
    });
    try {
      const response = await fetchApi(
        `/favorites/${mlsId}`,
        {},
        'delete',
        {},
        token,
      );
      const json = await response.json();
      if (response.ok) {
        track.unfavoritedListing(mlsId);
        return dispatch({
          deletedListing: json.deletedListing,
          messages: [json],
          type: types.FavoritesDeleteSuccess,
        });
      } else {
        return dispatch(
          forceLoginHelper(json, () => {
            dispatch({
              messages: [json],
              type: types.FavoritesDeleteFailure,
            });
          }),
        );
      }
    } catch (error) {
      dispatch({
        error,
        type: types.GenericError,
      });
    }
  };
}

/**
 * Set Show Favorites Modal Flag
 */
export function setShowFavoritesModal() {
  return async (dispatch: Dispatch<any>) => {
    return dispatch({
      type: types.SetShowFavoritesModal,
    });
  };
}

/**
 * Unset Show Favorites Modal Flag
 */
export function unsetShowFavoritesModal() {
  return async (dispatch: Dispatch<any>) => {
    return dispatch({
      type: types.UnsetShowFavoritesModal,
    });
  };
}
